import React, { useMemo, useState } from "react";
import { RemnatsProps } from "../api/ProductRemnants";
import {
  StyledTable,
  SmallTd90,
  SmallTd,
  SubTr,
  ArrowUpIcon,
} from "./RemnantsStyled";
import {
  Field,
  RemnantRowProps,
  isMpRemnantSendable,
  DisabledSend,
} from "./common";
import { Button, Form } from "react-bootstrap";
import { ColumnControl } from "./useColumnControl";
import type { RemnantsSendingFlags } from "./index";
import ArticleCell from "./v2/ArticleCell";
import MpSyncStatus from "./v2/MpSyncStatus";

const FullRemnantsItem = (props: {
  data: RemnantRowProps;
  remnants: RemnantRowProps[];
  columnControl: ColumnControl;
  update: (props: {
    article: string;
    value: Partial<RemnatsProps["product_remnants"]>;
  }) => Promise<boolean>;
  hoverCells: { column: string; row: string }[];
  updateDisabledSend: (
    props: {
      article: string;
      value: Partial<DisabledSend>;
    }[]
  ) => Promise<boolean>;
  updateMpNeedUpdate: (
    rows: {
      article: string;
      wb_data?: { need_update_stock: boolean };
      ozon_data?: { need_update_stock: boolean };
      ya_data?: { need_update_stock: boolean };
      sber_data?: { need_update_stock: boolean };
      ali_data?: { need_update_stock: boolean };
    }[]
  ) => Promise<boolean>;
  remnantsSendingFlags: RemnantsSendingFlags;
  retirementEnabled: boolean;
  onRetire: (
    article: string,
    marketplace: "wildberries" | "ozon",
    action: "retire" | "return"
  ) => Promise<boolean>;
}) => {
  const columnControl = props.columnControl;
  const [squeezed, setSqueezed] = useState(true);
  const [pendingMpKeys, setPendingMpKeys] = useState<Record<string, boolean>>({});
  const remnant = props.data;
  const hoveredCells = useMemo(() => {
    return props.hoverCells.filter((el) => el.row === props.data.article);
  }, [props.hoverCells, props.data.article]);
  const subRows = props.data.subRows;

  const rowNotError = props.data.rowNotError;
  const remnantValue = props.data.remnantValue;

  const formatLastUpdate = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const formatElapsed = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const now = new Date().getTime();
    const diffMs = Math.max(0, now - date.getTime());
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} д ${hours % 24} ч назад`;
    if (hours > 0) return `${hours} ч ${minutes % 60} м назад`;
    return `${minutes} м назад`;
  };

  const marketplaces = [
    {
      key: "wildberries",
      label: "Wildberries",
      stockPatchKey: "wb_data" as const,
      hasStockRow: Boolean(props.data.wb_data),
      needUpdate: props.data.wb_data?.need_update_stock === true,
      canSendToMp: isMpRemnantSendable({
        shareValue: remnant.share_in_wildberries,
        calculated: props.data.calculeted_wildberries,
      }),
      reserved: remnant.reserved_in_wildberries,
      shareValue: remnant.share_in_wildberries ?? "",
      shareKey: "share_in_wildberries" as const,
      calculated: props.data.calculeted_wildberries,
      disabled: props.data.disabled_send_to_wildberries,
      disabledKey: "disabled_send_to_wildberries" as const,
      lastStock: props.data.wb_data?.last_stock ?? null,
      lastUpdate: props.data.wb_data?.last_update ?? null,
      mpPresent: props.data.wb_data?.mp_stock ?? null,
      mpWarehouseSyncAt: props.data.wb_data?.mp_stock_sync_at ?? null,
      sendingEnabled: props.remnantsSendingFlags.wb,
      supportsSyncV2: true,
      targetStock: props.data.wb_data?.new_stock ?? null,
      quarantineAt: props.data.wb_data?.quarantine_at ?? null,
      quarantineReason: props.data.wb_data?.quarantine_reason ?? null,
      syncError: props.data.wb_data?.sync_error ?? null,
      quarantineResetKey: "wb_quarantine_at" as
        | "wb_quarantine_at"
        | "ozon_quarantine_at"
        | null,
      retirementState: props.data.wb_data?.retirement_state ?? null,
      retirementMp: "wildberries" as "wildberries" | "ozon" | null,
    },
    {
      key: "ozon",
      label: "Ozon",
      stockPatchKey: "ozon_data" as const,
      hasStockRow: Boolean(props.data.ozon_data),
      needUpdate: props.data.ozon_data?.need_update_stock === true,
      canSendToMp: isMpRemnantSendable({
        shareValue: remnant.share_in_ozon,
        calculated: props.data.calculeted_ozon,
      }),
      reserved: remnant.reserved_in_ozon,
      shareValue: remnant.share_in_ozon ?? "",
      shareKey: "share_in_ozon" as const,
      calculated: props.data.calculeted_ozon,
      disabled: props.data.disabled_send_to_ozon,
      disabledKey: "disabled_send_to_ozon" as const,
      lastStock: props.data.ozon_data?.last_stock ?? null,
      lastUpdate: props.data.ozon_data?.last_update ?? null,
      mpPresent: props.data.ozon_data?.mp_present ?? null,
      mpWarehouseSyncAt: props.data.ozon_data?.mp_warehouse_sync_at ?? null,
      sendingEnabled: props.remnantsSendingFlags.ozon,
      supportsSyncV2: true,
      targetStock: props.data.ozon_data?.new_stock ?? null,
      quarantineAt: props.data.ozon_data?.quarantine_at ?? null,
      quarantineReason: props.data.ozon_data?.quarantine_reason ?? null,
      syncError: props.data.ozon_data?.sync_error ?? null,
      quarantineResetKey: "ozon_quarantine_at" as
        | "wb_quarantine_at"
        | "ozon_quarantine_at"
        | null,
      retirementState: props.data.ozon_data?.retirement_state ?? null,
      retirementMp: "ozon" as "wildberries" | "ozon" | null,
    },
    {
      key: "ya",
      label: "Yandex Market",
      stockPatchKey: "ya_data" as const,
      hasStockRow: Boolean(props.data.ya_data),
      needUpdate: props.data.ya_data?.need_update_stock === true,
      canSendToMp: isMpRemnantSendable({
        shareValue: remnant.share_in_ya,
        calculated: props.data.calculeted_ya,
      }),
      reserved: remnant.reserved_in_ya,
      shareValue: remnant.share_in_ya ?? "",
      shareKey: "share_in_ya" as const,
      calculated: props.data.calculeted_ya,
      disabled: props.data.disabled_send_to_ya,
      disabledKey: "disabled_send_to_ya" as const,
      lastStock: props.data.ya_data?.last_stock ?? null,
      lastUpdate: props.data.ya_data?.last_update ?? null,
      mpPresent: null as number | null,
      mpWarehouseSyncAt: null as string | null,
      sendingEnabled: props.remnantsSendingFlags.ya,
      supportsSyncV2: false,
      targetStock: props.data.ya_data?.new_stock ?? null,
      quarantineAt: null as string | null,
      quarantineReason: null as string | null,
      syncError: null as string | null,
      quarantineResetKey: null as "wb_quarantine_at" | "ozon_quarantine_at" | null,
      retirementState: null as string | null,
      retirementMp: null as "wildberries" | "ozon" | null,
    },
    {
      key: "sber",
      label: "SberMegaMarket",
      stockPatchKey: "sber_data" as const,
      hasStockRow: Boolean(props.data.sber_data),
      needUpdate: props.data.sber_data?.need_update_stock === true,
      canSendToMp: isMpRemnantSendable({
        shareValue: remnant.share_in_sber,
        calculated: props.data.calculeted_sber,
      }),
      reserved: remnant.reserved_in_sber,
      shareValue: remnant.share_in_sber ?? "",
      shareKey: "share_in_sber" as const,
      calculated: props.data.calculeted_sber,
      disabled: props.data.disabled_send_to_sber,
      disabledKey: "disabled_send_to_sber" as const,
      lastStock: props.data.sber_data?.last_stock ?? null,
      lastUpdate: props.data.sber_data?.last_update ?? null,
      mpPresent: null as number | null,
      mpWarehouseSyncAt: null as string | null,
      sendingEnabled: props.remnantsSendingFlags.sber,
      supportsSyncV2: false,
      targetStock: props.data.sber_data?.new_stock ?? null,
      quarantineAt: null as string | null,
      quarantineReason: null as string | null,
      syncError: null as string | null,
      quarantineResetKey: null as "wb_quarantine_at" | "ozon_quarantine_at" | null,
      retirementState: null as string | null,
      retirementMp: null as "wildberries" | "ozon" | null,
    },
    {
      key: "ali",
      label: "AliExpress",
      stockPatchKey: "ali_data" as const,
      hasStockRow: Boolean(props.data.ali_data),
      needUpdate: props.data.ali_data?.need_update_stock === true,
      canSendToMp: isMpRemnantSendable({
        shareValue: remnant.share_in_ali,
        calculated: props.data.calculeted_ali,
      }),
      reserved: remnant.reserved_in_ali,
      shareValue: remnant.share_in_ali ?? "",
      shareKey: "share_in_ali" as const,
      calculated: props.data.calculeted_ali,
      disabled: props.data.disabled_send_to_ali,
      disabledKey: "disabled_send_to_ali" as const,
      lastStock: props.data.ali_data?.last_stock ?? null,
      lastUpdate: props.data.ali_data?.last_update ?? null,
      mpPresent: null as number | null,
      mpWarehouseSyncAt: null as string | null,
      sendingEnabled: props.remnantsSendingFlags.ali,
      supportsSyncV2: false,
      targetStock: props.data.ali_data?.new_stock ?? null,
      quarantineAt: null as string | null,
      quarantineReason: null as string | null,
      syncError: null as string | null,
      quarantineResetKey: null as "wb_quarantine_at" | "ozon_quarantine_at" | null,
      retirementState: null as string | null,
      retirementMp: null as "wildberries" | "ozon" | null,
    },
  ];

  const queueMpForSend = async (mp: (typeof marketplaces)[number]) => {
    if (
      !mp.hasStockRow ||
      mp.disabled ||
      mp.needUpdate ||
      !mp.sendingEnabled ||
      !mp.canSendToMp ||
      pendingMpKeys[mp.key]
    ) {
      return;
    }
    setPendingMpKeys((prev) => ({ ...prev, [mp.key]: true }));
    try {
      await props.updateMpNeedUpdate([
        {
          article: props.data.article,
          [mp.stockPatchKey]: { need_update_stock: true },
        } as {
          article: string;
          wb_data?: { need_update_stock: boolean };
          ozon_data?: { need_update_stock: boolean };
          ya_data?: { need_update_stock: boolean };
          sber_data?: { need_update_stock: boolean };
          ali_data?: { need_update_stock: boolean };
        },
      ]);
    } finally {
      setPendingMpKeys((prev) => ({ ...prev, [mp.key]: false }));
    }
  };

  return (
    <>
      <SubTr
        state={
          props.data.is_combo
            ? !rowNotError || !props.data.comboComposition?.length
              ? "absent"
              : ""
            : ""
        }
      >
        <td data-column="all" data-row={props.data.article}>
          <ArrowUpIcon down={squeezed} onClick={() => setSqueezed(!squeezed)} />{" "}
          <ArticleCell article={props.data.article} name={props.data.name} />
        </td>
        <td>{formatLastUpdate((props.data as any).last_remnant_update_at)}</td>
        {columnControl.remnantsChecked && (
          <td>
            {remnant.is_combo ? props.data.remnantValue : remnant.remnant}
          </td>
        )}
        {columnControl.reservedChecked && (
          <td>{props.data.totalReserved}</td>
        )}
        {columnControl.comboDistributionChecked && (
          <>
            <SmallTd90
              data-column={!remnant.is_combo && "combo"}
              data-row={!remnant.is_combo && props.data.article}
              data-selected={
                !remnant.is_combo &&
                !!hoveredCells.find((el) => el.column === "combo")
              }
            >
              {!remnant.is_combo && (
                <Field
                  value={remnant.share_in_combo || ""}
                  onUpdate={(v) =>
                    props.update({
                      article: props.data.article,
                      value: { share_in_combo: v },
                    })
                  }
                />
              )}
            </SmallTd90>
            <SmallTd90>
              {!remnant.is_combo ? (
                props.data.useInCombo ? (
                  props.data.remnantToCombo
                ) : (
                  <span style={{ color: "#666666" }}>нет в комбо</span>
                )
              ) : null}
            </SmallTd90>
            <SmallTd90>
              {!remnant.is_combo
                ? props.data.remnantForSingle +
                  (!props.data.useInCombo &&
                  typeof props.data.remnantToCombo === "number"
                    ? props.data.remnantToCombo
                    : 0)
                : null}
            </SmallTd90>
          </>
        )}
        {columnControl.calculationChecked && (
          <>
            <SmallTd90>
              {!props.data.disabled_send_to_wildberries
                ? props.data.calculeted_wildberries
                : ""}
            </SmallTd90>
            <SmallTd90>
              {!props.data.disabled_send_to_ozon
                ? props.data.calculeted_ozon
                : ""}
            </SmallTd90>
            <SmallTd90>
              {!props.data.disabled_send_to_ya ? props.data.calculeted_ya : ""}
            </SmallTd90>
            <SmallTd90>
              {!props.data.disabled_send_to_sber
                ? props.data.calculeted_sber
                : ""}
            </SmallTd90>
            <SmallTd90>
              {!props.data.disabled_send_to_ali
                ? props.data.calculeted_ali
                : ""}
            </SmallTd90>
          </>
        )}
      </SubTr>
      {!squeezed ? (
        props.data.comboComposition && props.data.comboComposition.length ? (
          subRows.map((el) => (
            <SubTr
              state={
                !el.finded
                  ? "absent"
                  : el.remnantDevValue === remnantValue
                  ? "minimal"
                  : ""
              }
            >
              <td style={{ paddingLeft: 24 }}>{el.name}</td>
              <td colSpan={20}>
                {el.remnantToCombo} / {el.value} = {el.remnantDevValue}
              </td>
            </SubTr>
          ))
        ) : (
          <SubTr state={"absent"}>
            <td colSpan={20}>Не указан состав комбо набора</td>
          </SubTr>
        )
      ) : null}
      {!squeezed && (
        <>
          <SubTr state={""}>
            <td style={{ paddingLeft: 24, fontWeight: 600 }}>Маркетплейсы</td>
            <td colSpan={20}>
              Резерв | Формула распределения | Расчёт | Блокировка отправки
            </td>
          </SubTr>
          {marketplaces.map((mp) => (
            <SubTr key={mp.key} state={""}>
              <td style={{ paddingLeft: 24 }}>{mp.label}</td>
              <td colSpan={20}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(80px, 1fr) minmax(140px, 1.4fr) minmax(110px, 1fr) minmax(140px, 1.4fr) minmax(80px, 0.8fr)",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: "#666" }}>Резерв</div>
                    <strong>{mp.reserved ?? 0}</strong>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#666" }}>
                      Последняя синхронизация
                    </div>
                    <span>
                      {formatLastUpdate(mp.lastUpdate) || "—"}
                      {mp.lastUpdate ? ` · ${formatElapsed(mp.lastUpdate)}` : ""}
                    </span>
                    {mp.mpWarehouseSyncAt && (
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                        МП: {formatLastUpdate(mp.mpWarehouseSyncAt)}{" "}
                        · {formatElapsed(mp.mpWarehouseSyncAt)}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#666" }}>
                      Отправленный остаток
                    </div>
                    <strong>{mp.lastStock ?? "-"}</strong>
                    {mp.mpPresent !== null && (
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                        В МП: <strong>{mp.mpPresent}</strong>
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#666" }}>Формула</div>
                    {mp.disabled ? (
                      <Form.Control
                        className="input"
                        value={mp.shareValue}
                        disabled
                        readOnly
                      />
                    ) : (
                      <Field
                        value={mp.shareValue}
                        onUpdate={(v) =>
                          props.update({
                            article: props.data.article,
                            value: { [mp.shareKey]: v },
                          })
                        }
                      />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#666" }}>
                      Расчёт / блокировка
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <strong
                        style={{ minWidth: 32, display: "inline-block" }}
                      >
                        {mp.disabled ? "" : mp.calculated ?? "-"}
                      </strong>
                      <Form.Check
                        type="checkbox"
                        checked={mp.disabled ?? false}
                        onChange={() =>
                          props.updateDisabledSend([
                            {
                              article: props.data.article,
                              value: { [mp.disabledKey]: !mp.disabled },
                            },
                          ])
                        }
                        label="Блокировка"
                        style={{ marginLeft: 8, marginBottom: 0 }}
                      />
                    </div>
                    {mp.supportsSyncV2 && (
                      <MpSyncStatus
                        remnant1cUpdatedAt={props.data.last_remnant_update_at}
                        mpStockSyncAt={mp.mpWarehouseSyncAt}
                        mpStockActual={mp.mpPresent}
                        targetStock={mp.targetStock}
                        lastSentAt={mp.lastUpdate}
                        needUpdate={mp.needUpdate}
                        disabled={mp.disabled}
                        quarantineAt={mp.quarantineAt}
                        quarantineReason={mp.quarantineReason}
                        syncError={mp.syncError}
                        canSend={mp.canSendToMp}
                        onResetQuarantine={
                          mp.quarantineResetKey
                            ? () =>
                                props.updateDisabledSend([
                                  {
                                    article: props.data.article,
                                    value: {
                                      [mp.quarantineResetKey as string]: null,
                                    },
                                  },
                                ])
                            : undefined
                        }
                      />
                    )}
                    {props.retirementEnabled &&
                      mp.supportsSyncV2 &&
                      mp.retirementMp && (
                        <div style={{ marginTop: 6, fontSize: 11 }}>
                          {mp.retirementState ? (
                            <>
                              <span style={{ color: "#c0392b" }}>
                                Вывод с МП:{" "}
                                {mp.retirementState === "zeroing"
                                  ? "обнуление…"
                                  : mp.retirementState === "disabled"
                                  ? "отключён"
                                  : mp.retirementState}
                              </span>
                              <Button
                                size="sm"
                                variant="outline-success"
                                style={{
                                  marginLeft: 8,
                                  padding: "0 6px",
                                  fontSize: 11,
                                }}
                                onClick={() =>
                                  props.onRetire(
                                    props.data.article,
                                    mp.retirementMp as "wildberries" | "ozon",
                                    "return"
                                  )
                                }
                              >
                                Вернуть товар
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              style={{ padding: "0 6px", fontSize: 11 }}
                              onClick={() =>
                                props.onRetire(
                                  props.data.article,
                                  mp.retirementMp as "wildberries" | "ozon",
                                  "retire"
                                )
                              }
                            >
                              Вывести с МП (обнулить и отключить)
                            </Button>
                          )}
                        </div>
                      )}
                    <div style={{ marginTop: 8, gridColumn: "1 / -1" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={mp.needUpdate}
                          disabled={!mp.hasStockRow}
                          onChange={async () => {
                            if (!mp.hasStockRow) return;
                            const next = !mp.needUpdate;
                            if (next && mp.disabled) {
                              return;
                            }
                            await props.updateMpNeedUpdate([
                              {
                                article: props.data.article,
                                [mp.stockPatchKey]: { need_update_stock: next },
                              } as {
                                article: string;
                                wb_data?: { need_update_stock: boolean };
                                ozon_data?: { need_update_stock: boolean };
                                ya_data?: { need_update_stock: boolean };
                                sber_data?: { need_update_stock: boolean };
                                ali_data?: { need_update_stock: boolean };
                              },
                            ]);
                          }}
                          label="Очередь на отправку (need_update) для этого МП"
                          style={{ marginBottom: 0 }}
                        />
                        <Button
                          size="sm"
                          disabled={
                            !mp.hasStockRow ||
                            mp.disabled ||
                            mp.needUpdate ||
                            !mp.sendingEnabled ||
                            !mp.canSendToMp ||
                            Boolean(pendingMpKeys[mp.key])
                          }
                          onClick={() => queueMpForSend(mp)}
                        >
                          Обновить в МП
                        </Button>
                      </div>
                      {mp.hasStockRow && !mp.sendingEnabled && (
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                          Ручная отправка отключена feature flag
                        </div>
                      )}
                      {mp.hasStockRow && !mp.disabled && !mp.canSendToMp && (
                        <div style={{ fontSize: 11, color: "#c0392b", marginTop: 2 }}>
                          Укажите корректную формулу
                        </div>
                      )}
                      {mp.disabled && mp.hasStockRow && (
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                          При блокировке отправки можно только снять очередь;
                          включить — сначала снимите блокировку
                        </div>
                      )}
                      {!mp.hasStockRow && (
                        <div style={{ fontSize: 11, color: "#888" }}>
                          Нет строки остатков МП — сначала привяжите товар
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </td>
            </SubTr>
          ))}
        </>
      )}
    </>
  );
};

export default FullRemnantsItem;
